import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const digitalFeatures = [
  {
    id: 'online-termin',
    title: 'Online-Terminvereinbarung',
    description: 'Ihren Wunschtermin direkt im Kalender wählen.',
    image: '/images/4_Termin_Online.webp'
  },
  {
    id: 'erezept',
    title: 'eRezept & Folgerezepte',
    description: 'Rezepte bequem von zu Hause anfordern.',
    image: '/images/Content_Behandlung_5.webp'
  },
  {
    id: 'eau',
    title: 'eAU – Digitale Krankschreibung',
    description: 'Automatisierte Übermittlung an Ihre Krankenkasse.',
    image: '/images/Content_Behandlung_6.webp'
  },
  {
    id: 'formulare',
    title: 'Formulare & Downloads',
    description: 'Wichtige Dokumente vorab herunterladen.',
    image: '/images/Content_Behandlung_7.webp'
  },
  {
    id: 'erinnerung',
    title: 'Erinnerungsservice',
    description: 'Automatische Erinnerung an Vorsorge und Impfungen.',
    image: '/images/Content_Behandlung_8.webp'
  }
];

export const DigitalPage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <PageHero
        title="Patienten-Service & Digitale Angebote"
        subtitle="Organisation, Service und digitale Wege – einfach, sicher und persönlich."
        backgroundImage={pageHeroImages.digital}
        ctaText="Online-Termin buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('services')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Services
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('online-termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Online-Termin
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('erezept')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              eRezept
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('eau')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              eAU
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid mit klickbaren Karten */}
      <section id="services" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere Serviceangebote
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {digitalFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all"
                onClick={() => scrollToSection(feature.id)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                  <p className="text-[#032925]/70 text-sm mb-4">{feature.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                    Mehr erfahren
                    <ArrowDown className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Online-Termin Content */}
      <div id="online-termin">
        <ContentSection
          title="Online-Terminvereinbarung"
          content="Buchen Sie Ihren Wunschtermin bequem online – rund um die Uhr und ohne Wartezeit am Telefon. Wählen Sie Datum, Uhrzeit und Anlass direkt in unserem Kalender. Sie erhalten eine Bestätigung per E-Mail und können den Termin bei Bedarf auch online ändern."
          image="/images/4_Termin_Online.webp"
          backgroundColor="white"
        />
      </div>

      {/* eRezept Content */}
      <div id="erezept">
        <ContentSection
          title="eRezept & Folgerezepte"
          content="Benötigen Sie ein Folgerezept für Ihre Dauermedikation? Fordern Sie es einfach digital an – wir prüfen Ihre Anfrage und stellen das eRezept zeitnah aus. Sie können es direkt in Ihrer Apotheke einlösen, ohne vorher in die Praxis kommen zu müssen."
          image="/images/Content_Behandlung_5.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* eAU Content */}
      <div id="eau">
        <ContentSection
          title="eAU – Digitale Krankschreibung"
          content="Die elektronische Arbeitsunfähigkeitsbescheinigung wird automatisch an Ihre Krankenkasse übermittelt. Sie müssen sich nicht mehr um den Versand kümmern. Auf Wunsch erhalten Sie einen Ausdruck für Ihre Unterlagen."
          image="/images/Content_Behandlung_6.webp"
          backgroundColor="white"
        />
      </div>

      {/* Formulare Content */}
      <div id="formulare">
        <ContentSection
          title="Formulare & Downloads"
          content="Laden Sie wichtige Dokumente und Formulare vorab herunter. So können Sie Anamnesebögen, Einwilligungserklärungen und andere Unterlagen in Ruhe zu Hause ausfüllen und bei Ihrem Termin mitbringen – das spart Zeit für alle."
          image="/images/Content_Behandlung_7.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Erinnerungsservice Content */}
      <div id="erinnerung">
        <ContentSection
          title="Erinnerungsservice"
          content="Vergessen Sie keine wichtigen Vorsorgeuntersuchungen oder Impfauffrischungen mehr. Unser Erinnerungsservice informiert Sie rechtzeitig per E-Mail oder SMS, wenn ein Termin ansteht."
          image="/images/Content_Behandlung_8.webp"
          backgroundColor="white"
        />
      </div>

      {/* Schnell organisiert */}
      <ContentSection
        title="Schnell organisiert – weniger Wartezeit"
        content="Mit unseren digitalen Serviceangeboten können Sie Termine vereinbaren, Rezepte anfragen und wichtige Anliegen bequem von zu Hause aus erledigen. Das spart Zeit und macht den Praxisbesuch effizienter."
        image="/images/Schnell_organisiert_opt.webp"
        imagePosition="left"
        backgroundColor="gray"
      />

      {/* Persönlicher Service */}
      <section className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                Persönlicher Service bleibt wichtig
              </h2>
              <p className="text-[#032925]/80 leading-relaxed mb-6">
                Trotz moderner Technik bleibt der persönliche Kontakt für uns zentral. Unser Praxisteam unterstützt Sie gerne telefonisch oder vor Ort. Digitale Angebote ergänzen unseren Service – sie ersetzen nicht das persönliche Gespräch.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/termin" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Kontakt & Anfahrt
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/4_Termin_Beratung.webp"
                  alt="Beratung"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SEOContent
        title="Patientenservice und digitale Hausarztpraxis in Diez"
        content="Die Gemeinschaftspraxis Dres. von Bergh im Medicum Diez bietet Online-Terminvereinbarung, eRezept, digitale Krankschreibung (eAU) und persönliche Serviceangebote für Patientinnen und Patienten im Rhein-Lahn-Kreis. Nutzen Sie unsere digitalen Services für mehr Komfort im Praxisalltag."
      />

      <div id="termin">
        <CTASection
          title="Service nutzen oder Termin vereinbaren"
          subtitle="Nutzen Sie unsere digitalen Angebote oder kontaktieren Sie uns persönlich."
          buttonText="Online-Termin buchen"
        />
      </div>
    </Layout>
  );
};

export default DigitalPage;
